import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut, KeyRound, Camera, Loader2, ShieldCheck, Trash2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { AUTH } from "@/constants/testIds";
import TwoFactorSetup from "@/components/TwoFactorSetup";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const resolveAvatar = (url) => {
  if (!url) return null;
  return url.startsWith("http") ? url : `${BACKEND}${url}`;
};

export default function ProfileMenu() {
  const { user, refresh, logout } = useAuth();
  const nav = useNavigate();
  const fileInput = useRef(null);
  const [photoBusy, setPhotoBusy] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);
  const [pw, setPw] = useState({ current: "", new1: "", new2: "" });
  const [pwBusy, setPwBusy] = useState(false);
  const [twoFaOpen, setTwoFaOpen] = useState(false);

  if (!user) return null;

  const initials = (user.name || "?")
    .split(" ")
    .map((s) => s[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const avatarSrc = resolveAvatar(user.avatar_url);

  const onPickPhoto = () => fileInput.current?.click();

  const onPhotoChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error("Photo must be smaller than 5 MB");
      return;
    }
    setPhotoBusy(true);
    try {
      const form = new FormData();
      form.append("file", file);
      // `purpose` is a query param on the backend, not a form field.
      const up = await api.post("/files/upload?purpose=avatar", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      await api.patch("/auth/me", { avatar_url: up.data.url });
      await refresh();
      toast.success("Profile photo updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Photo upload failed");
    } finally {
      setPhotoBusy(false);
    }
  };

  const onSubmitPw = async (e) => {
    e.preventDefault();
    if (pw.new1.length < 8) {
      toast.error("New password must be at least 8 characters");
      return;
    }
    if (pw.new1 !== pw.new2) {
      toast.error("New passwords do not match");
      return;
    }
    setPwBusy(true);
    try {
      await api.post("/auth/change-password", {
        current_password: pw.current,
        new_password: pw.new1,
      });
      toast.success("Password updated");
      setPw({ current: "", new1: "", new2: "" });
      setPwOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Password change failed");
    } finally {
      setPwBusy(false);
    }
  };

  const onSignOut = async () => {
    await logout();
    nav("/");
  };

  return (
    <>
      <input
        ref={fileInput}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={onPhotoChange}
        data-testid="profile-photo-file"
      />
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            className="rounded-full outline-none focus-visible:ring-2 focus-visible:ring-primary"
            data-testid="profile-menu-trigger"
            aria-label="Open profile menu"
          >
            <Avatar className="w-9 h-9 border border-border">
              {avatarSrc ? <AvatarImage src={avatarSrc} alt={user.name} /> : null}
              <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                {initials}
              </AvatarFallback>
            </Avatar>
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-64" data-testid="profile-menu">
          <DropdownMenuLabel className="flex flex-col">
            <span className="text-sm font-semibold truncate">{user.name}</span>
            <span className="text-[11px] text-muted-foreground truncate">{user.email}</span>
            <span className="text-[10px] text-muted-foreground uppercase tracking-wider mt-0.5">
              {user.role?.replace("_", " ")}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={onPickPhoto}
            disabled={photoBusy}
            data-testid="profile-menu-change-photo"
          >
            {photoBusy ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <Camera className="w-4 h-4 mr-2" />
            )}
            {photoBusy ? "Uploading…" : "Change profile photo"}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setPwOpen(true)}
            data-testid="profile-menu-change-password"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            Change password
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => setTwoFaOpen(true)}
            data-testid="profile-menu-2fa"
          >
            <ShieldCheck className="w-4 h-4 mr-2" />
            Two-factor authentication
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem
            onClick={() => nav("/delete-account")}
            data-testid="profile-menu-delete-account"
            className="text-rose-600 focus:text-rose-600 dark:text-rose-400 dark:focus:text-rose-400"
          >
            <Trash2 className="w-4 h-4 mr-2" />
            Delete account
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={onSignOut} data-testid={AUTH.logoutBtn}>
            <LogOut className="w-4 h-4 mr-2" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={pwOpen} onOpenChange={setPwOpen}>
        <DialogContent data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>
              Choose a strong new password with at least 8 characters.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmitPw} className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="pw-current">Current password</Label>
              <Input
                id="pw-current"
                type="password"
                required
                value={pw.current}
                onChange={(e) => setPw((p) => ({ ...p, current: e.target.value }))}
                data-testid="pw-current-input"
                autoFocus
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-new1">New password</Label>
              <Input
                id="pw-new1"
                type="password"
                required
                minLength={8}
                value={pw.new1}
                onChange={(e) => setPw((p) => ({ ...p, new1: e.target.value }))}
                data-testid="pw-new-input"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-new2">Confirm new password</Label>
              <Input
                id="pw-new2"
                type="password"
                required
                minLength={8}
                value={pw.new2}
                onChange={(e) => setPw((p) => ({ ...p, new2: e.target.value }))}
                data-testid="pw-confirm-input"
              />
            </div>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setPwOpen(false)}
                data-testid="pw-cancel-btn"
              >
                Cancel
              </Button>
              <Button type="submit" disabled={pwBusy} data-testid="pw-submit-btn">
                {pwBusy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                {pwBusy ? "Updating…" : "Update password"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <TwoFactorSetup open={twoFaOpen} onOpenChange={setTwoFaOpen} />
    </>
  );
}
